import React, { useEffect, useState, useMemo } from "react";
import Sidebar from "components/Sidebar/Sidebar.js";
import { Spinner } from "components/Spinner";
import config from "services/config";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Send, Mail, Users, CheckCircle, Search, ArrowLeft } from "lucide-react";

const CARD = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #e8ecf1",
  overflow: "hidden",
};

const TH = {
  padding: "10px 16px",
  fontSize: 11,
  fontWeight: 600,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  textAlign: "left",
  borderBottom: "1px solid #e8ecf1",
};

export function MarketingListGroupSend() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [members, setMembers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [membersRes, campaignsRes] = await Promise.all([
          axios.get(`${config.url}/members/showall`),
          axios.get(`${config.url}/marketing/list`, {
            headers: { Authorization: `Basic ${process.env.REACT_APP_BASIC_AUTH}` },
          }),
        ]);

        const currentDate = new Date();
        const allMembers = membersRes.data || [];
        const expired = allMembers.filter(
          (m) =>
            m.enabled &&
            m.subscription?.endDate &&
            new Date(m.subscription.endDate) < currentDate
        );

        setMembers(expired);
        const groupCampaigns = (campaignsRes.data || []).filter((c) => c.targetType === "group");
        setCampaigns(groupCampaigns);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredMembers = useMemo(() => {
    if (!searchTerm) return members;
    const q = searchTerm.toLowerCase();
    return members.filter(
      (m) =>
        m.name?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q) ||
        m.id_number?.includes(q)
    );
  }, [members, searchTerm]);

  const handleToggleMember = (id) => {
    setSelectedMembers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allSelected = filteredMembers.length > 0 && selectedMembers.length === filteredMembers.length;
  const someSelected = selectedMembers.length > 0 && !allSelected;

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(filteredMembers.map((m) => m._id));
    }
  };

  const handleSend = async () => {
    if (!selectedCampaign || selectedMembers.length === 0) return;
    try {
      setSending(true);
      await axios.post(
        `${config.url}/marketing/group-send`,
        { campaignId: selectedCampaign._id, memberIds: selectedMembers },
        { headers: { Authorization: `Basic ${process.env.REACT_APP_BASIC_AUTH}` } }
      );
      alert(`Campana enviada a ${selectedMembers.length} miembro(s)`);
      setSelectedMembers([]);
    } catch (error) {
      console.error("Error sending:", error);
      alert("Error al enviar la campana");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: 210, minHeight: "100vh", background: "#fff", padding: "24px 32px 32px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <div
            onClick={() => navigate("/admin/marketing")}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#0353a4")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e2e8f0")}
          >
            <ArrowLeft size={16} color="#64748b" />
          </div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", margin: 0 }}>
              Enviar Campana
            </h1>
            <p style={{ fontSize: 13, color: "#94a3b8", margin: 0, marginTop: 2 }}>
              Selecciona una campana y los miembros destinatarios
            </p>
          </div>
        </div>

        {/* Layout */}
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
          {/* Left: Campaign selector */}
          <div style={{ width: 340, flexShrink: 0 }}>
            <div style={{ ...CARD, padding: "20px" }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", margin: 0, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <Mail size={16} color="#0353a4" />
                Seleccionar Campana
              </p>

              {campaigns.length === 0 ? (
                <p style={{ fontSize: 13, color: "#94a3b8", textAlign: "center", padding: "24px 0" }}>
                  No hay campanas de grupo disponibles
                </p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {campaigns.map((c) => {
                    const isSelected = selectedCampaign?._id === c._id;
                    return (
                      <div
                        key={c._id}
                        onClick={() => setSelectedCampaign(c)}
                        style={{
                          padding: "14px 16px",
                          borderRadius: 10,
                          border: isSelected ? "2px solid #0353a4" : "1px solid #e8ecf1",
                          background: isSelected ? "#f0f6ff" : "#fff",
                          cursor: "pointer",
                          transition: "all 0.15s",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <div
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: 8,
                            background: c.content?.imgUrl
                              ? `url(${c.content.imgUrl}) center/cover`
                              : "#f1f5f9",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {!c.content?.imgUrl && <Mail size={18} color="#cbd5e1" />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#0f172a",
                              margin: 0,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {c.title}
                          </p>
                          <p
                            style={{
                              fontSize: 12,
                              color: "#64748b",
                              margin: 0,
                              marginTop: 2,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {c.subject || "Sin asunto"}
                          </p>
                        </div>
                        {isSelected && <CheckCircle size={18} color="#0353a4" />}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Send button */}
              {selectedCampaign && (
                <button
                  onClick={handleSend}
                  disabled={selectedMembers.length === 0 || sending}
                  style={{
                    width: "100%",
                    marginTop: 20,
                    padding: "12px 0",
                    borderRadius: 10,
                    border: "none",
                    background: selectedMembers.length === 0 ? "#e2e8f0" : "#0353a4",
                    color: selectedMembers.length === 0 ? "#94a3b8" : "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: selectedMembers.length === 0 ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    transition: "all 0.15s",
                    opacity: sending ? 0.7 : 1,
                  }}
                >
                  <Send size={15} />
                  {sending
                    ? "Enviando..."
                    : `Enviar a ${selectedMembers.length} miembro(s)`}
                </button>
              )}
            </div>
          </div>

          {/* Right: Members table */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...CARD }}>
              {/* Table header bar */}
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #e8ecf1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Users size={16} color="#0353a4" />
                  <p style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", margin: 0 }}>
                    Miembros Vencidos
                  </p>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#dc2626",
                      background: "#fef2f2",
                      padding: "2px 8px",
                      borderRadius: 20,
                    }}
                  >
                    {filteredMembers.length}
                  </span>
                  {selectedMembers.length > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#0353a4",
                        background: "#eff6ff",
                        padding: "2px 8px",
                        borderRadius: 20,
                      }}
                    >
                      {selectedMembers.length} seleccionados
                    </span>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "#f8fafc",
                    border: "1px solid #e8ecf1",
                    borderRadius: 8,
                    padding: "6px 12px",
                    width: 240,
                  }}
                >
                  <Search size={14} color="#94a3b8" />
                  <input
                    placeholder="Buscar miembro..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontSize: 13,
                      color: "#334155",
                      width: "100%",
                    }}
                  />
                </div>
              </div>

              {/* Table */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th style={{ ...TH, width: 44, padding: "10px 12px" }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => { if (el) el.indeterminate = someSelected; }}
                          onChange={handleToggleAll}
                          style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#0353a4" }}
                        />
                      </th>
                      <th style={TH}>Nombre</th>
                      <th style={TH}>Email</th>
                      <th style={TH}>RUT</th>
                      <th style={TH}>Vencimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: "40px 0", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
                          No se encontraron miembros
                        </td>
                      </tr>
                    ) : (
                      filteredMembers.map((m, i) => {
                        const checked = selectedMembers.includes(m._id);
                        const endDate = m.subscription?.endDate
                          ? new Date(m.subscription.endDate).toLocaleDateString("es-CL")
                          : "N/A";
                        return (
                          <tr
                            key={m._id}
                            onClick={() => handleToggleMember(m._id)}
                            style={{
                              borderBottom: i < filteredMembers.length - 1 ? "1px solid #f1f5f9" : "none",
                              cursor: "pointer",
                              transition: "background 0.1s",
                              background: checked ? "#f0f6ff" : "transparent",
                            }}
                            onMouseEnter={(e) => {
                              if (!checked) e.currentTarget.style.background = "#f8fafc";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = checked ? "#f0f6ff" : "transparent";
                            }}
                          >
                            <td style={{ padding: "10px 12px" }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {}}
                                style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#0353a4" }}
                              />
                            </td>
                            <td style={{ padding: "10px 16px", fontSize: 14, fontWeight: 500, color: "#0f172a" }}>
                              {m.name || "Sin nombre"}
                            </td>
                            <td style={{ padding: "10px 16px", fontSize: 13, color: "#64748b" }}>
                              {m.email || "Sin email"}
                            </td>
                            <td style={{ padding: "10px 16px", fontSize: 13, color: "#64748b", fontFamily: "monospace" }}>
                              {m.id_number || "-"}
                            </td>
                            <td style={{ padding: "10px 16px" }}>
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 500,
                                  color: "#dc2626",
                                  background: "#fef2f2",
                                  padding: "3px 8px",
                                  borderRadius: 4,
                                }}
                              >
                                {endDate}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
